import React from "react";
import { Search, Brain, Settings, BarChart3, ShieldCheck } from "lucide-react";

const PILLAR_ITEMS = [
  {
    icon: Search,
    color: "#0D9488",
    title: "Understand",
    desc: "Language, documents, data, context, images.",
  },
  {
    icon: Brain,
    color: "#9333EA",
    title: "Reason",
    desc: "Analyze, plan, evaluate and decide.",
  },
  {
    icon: Settings,
    color: "#2563EB",
    title: "Act",
    desc: "Use systems, tools, APIs and workflows.",
  },
  {
    icon: BarChart3,
    color: "#F59E0B",
    title: "Learn & Evolve",
    desc: "Improve using feedback, changing information and newer capabilities.",
  },
];

const GOVERNANCE_POINTS = [
  "Safe",
  "Responsible",
  "Transparent",
  "Aligned to your goals",
];

export const TransformPillars = () => {
  return (
    <section
      className="relative w-full overflow-hidden py-20 lg:py-24"
      style={{ backgroundColor: "rgba(0, 17, 39, 1)" }}
    >
      {/* Background abstract graphic darkened with rgba(0, 17, 39, 1) matching exact screenshot */}
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/images/transform/understand-bg.png')`,
          opacity: 0.35,
          mixBlendMode: "screen",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
        {/* Header row: Title left, description with vertical line on right */}
        <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <div className="flex flex-col gap-2.5">
            <p className="text-[11px] font-bold uppercase tracking-[2.4px] text-[#94A3B8]">
              THE POWER BEHIND TRANSFORMATION
            </p>
            <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight text-white">
              Understand. Reason. Act. Learn.
            </h2>
          </div>

          <div className="max-w-[520px] lg:border-l lg:border-[#334E68] lg:pl-8">
            <p className="text-[14px] sm:text-[14.5px] leading-[23px] text-[#CBD5E1]">
              Cognitive AI goes beyond automation. It understands context, reasons
              through complexity, takes action and learns — creating a virtuous cycle
              of improving outcomes.
            </p>
          </div>
        </div>

        {/* 5 Cards Row */}
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 lg:mt-16">
          {/* First 4 cards styled exactly per Figma inspection specs without hover effects */}
          {PILLAR_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="flex flex-col items-center rounded-[16px] text-center"
                style={{
                  minHeight: "216.5px",
                  paddingTop: "24px",
                  paddingRight: "24px",
                  paddingBottom: "43.5px",
                  paddingLeft: "24px",
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)",
                  border: "1px solid rgba(226, 232, 240, 0.7)",
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(40px)",
                }}
              >
                {/* White circular icon container */}
                <div className="mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-white shadow-md">
                  <Icon
                    className="h-6 w-6"
                    style={{ color: item.color }}
                    strokeWidth={2.3}
                  />
                </div>

                <h3 className="mb-2 text-[17px] font-bold text-white">
                  {item.title}
                </h3>
                <p className="text-[13px] leading-[19px] text-[#E2E8F0]">
                  {item.desc}
                </p>
              </div>
            );
          })}

          {/* 5th Card: With Human Governance - matching dark blue glassmorphism theme */}
          <div
            className="flex flex-col justify-between rounded-[16px] p-6 text-left"
            style={{
              minHeight: "216.5px",
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)",
              border: "1px solid rgba(226, 232, 240, 0.7)",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(40px)",
            }}
          >
            <div>
              {/* Header with circular icon */}
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-white shadow-md">
                  <ShieldCheck className="h-5 w-5 text-[#2563EB]" strokeWidth={2.4} />
                </div>
                <h3 className="text-[15px] font-bold leading-tight text-white">
                  With Human Governance
                </h3>
              </div>

              {/* Bullet list */}
              <ul className="flex flex-col gap-2.5 pt-1 text-[13px] text-[#E2E8F0]">
                {GOVERNANCE_POINTS.map((pt) => (
                  <li key={pt} className="flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#00D2EE] shrink-0" />
                    <span className="font-medium">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformPillars;
